import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import { IconButton, Link, Stack, Typography } from '@mui/material';
import moment from 'moment';
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as PDFIcon } from '../../../../../assets/icons/pdfNew.svg';
import { ReactComponent as LinkIcon } from '../../../../../assets/icons/link.svg';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { FileStructureContext } from '../../../../../contexts/FileStructureContext';
import useRoute from '../../../../../hooks/useRoute';
import { updateFileFunc } from '../../../../../lib/helper';
import theme from '../../../../../theme';
import { CompanyFile, Dashboard } from '../../../../../types/files';
import DeckListModal from '../../../../modals/tables/dashboards/rows/DeckListModal';
import CompanyMiniDeckViewer from '../../../../molecules/dashboard-deck/CompanyMiniDeckViewer';
import useStyles from "../styles";

const DeckTeaserCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { fileStructure } = useContext(FileStructureContext);
    const { redirectToDashboard } = useRoute();

    const [deckViewerModalOpen, setDeckViewerModalOpen] = useState<boolean>(false);
    const [deckListModalOpen, setDeckListModalOpen] = useState<boolean>(false);
    const [fieldHover, setFieldHover] = useState<boolean>(false);
    const deckRef = useRef<HTMLElement>(null);

    const sanitizeUrl = (originUrl: string) => originUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');

    const files = useMemo(() => fileStructure
        .filter(file => !file.isDirectory && file.dashboardId === dashboard?.id)
        .sort((dA, dB) => moment(dB.createdAt).diff(moment(dA.createdAt)))
    , [dashboard, fileStructure]);

    const defaultFile = useMemo(() => files.find(file => file.isDeck) ?? files[0], [files]);

    const handleSetDeck = useCallback(async (file: CompanyFile) => {
        const deckFiles = files?.filter(f => f.isDeck);
        const promises: Promise<CompanyFile>[] = [];

        deckFiles?.forEach(dF => {
            promises.push(updateFileFunc({
                id: dF.id,
                group: userGroup,
                documentCategory: dF.documentCategory,
                status: dF.status,
                dashboardId: dF.dashboardId,
                marketMetrics: dF.marketMetrics,
                isDeck: false,
            }))
        });

        promises.push(updateFileFunc({
            id: file.id,
            group: userGroup,
            documentCategory: file.documentCategory,
            status: file.status,
            dashboardId: file.dashboardId,
            marketMetrics: file.marketMetrics,
            isDeck: true,
        }));

        await Promise.all(promises);
    }, [files, userGroup]);

    useEffect(() => {
        if (!!files[0] && !files[0]?.isDeck)
            handleSetDeck(files[0]);
        //eslint-disable-next-line
    }, [files]);

    return (<>
        <Stack width="100%" ref={deckRef} onMouseLeave={() => setFieldHover(false)}>
            {(!!defaultFile) ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={fieldHover ? 'space-between' : 'flex-start'} width="100%"
                    onMouseEnter={() => setFieldHover(true)}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%">
                        <PDFIcon className={classes.icon} fill="#7bd4d4" />
                        <Link onClick={() => redirectToDashboard(dashboard.id, ['files', defaultFile.id].join('/'))}
                            underline="hover" target="_self" sx={{ cursor: 'pointer' }}>
                            <Typography className={classes.url} sx={{ maxWidth: `${fieldHover ? 14 : 16}ch !important`}}>
                                {defaultFile.name}
                            </Typography>
                        </Link>
                    </Stack>
                    {fieldHover && (
                        <Stack direction="row" spacing="4px" alignItems="center" justifyContent="flex-end" width="100%">
                            <IconButton disableRipple
                                onClick={() => setDeckListModalOpen(true) }
                                sx={{ padding: 'unset !important'}}>
                                <ExpandMoreIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                            </IconButton>
                            <IconButton disableRipple
                                onClick={() => setDeckViewerModalOpen(true)}
                                sx={{ padding: 'unset !important'}}>
                                <LaunchIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            ): !!dashboard.externalLinks?.length ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={fieldHover ? 'space-between' : 'flex-start'} width="100%"
                    onMouseEnter={() => setFieldHover(!!defaultFile)}>
                    <Stack direction="row" spacing="4px" alignItems="center" justifyContent="flex-start" width="100%">
                        <LinkIcon className={classes.icon28} stroke={theme.colors.neutral['500']} />
                        <Typography component="a"
                            className={classes.url}
                            href={dashboard?.externalLinks?.at(-1)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ maxWidth: `${fieldHover ? 16 : 18}ch !important`}}>
                            {sanitizeUrl(dashboard?.externalLinks?.at(-1) || '')}
                        </Typography>
                    </Stack>
                    {fieldHover && (
                        <IconButton disableRipple
                            onClick={() => setDeckViewerModalOpen(true)}
                            sx={{ padding: 'unset !important'}}>
                            <LaunchIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                        </IconButton>
                    )}
                </Stack>
            ) : (<Typography className={classes.url} color={`${theme.colors.neutral['500']} !important`}>{'No deck/teaser'}</Typography>)}
        </Stack>
        {deckViewerModalOpen && (
            <CompanyMiniDeckViewer
                open={deckViewerModalOpen}
                defaultDeck={defaultFile}
                externalDeck={dashboard?.externalLinks?.at(-1)}
                onClose={() => setDeckViewerModalOpen(false) } />
        )}
        {deckListModalOpen && (
            <DeckListModal open={deckListModalOpen}
                dashboard={dashboard}
                files={files}
                anchorElement={deckRef.current}
                onSetDeck={handleSetDeck}
                onClose={() => setDeckListModalOpen(false)} />
        )}
    </>);
}

export default memo(DeckTeaserCell);
